// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';

export default function PEQuestionPoolArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  nestIndex,
  possiblePages
}) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `content.core.${nestIndex}.content`
  });
  if (possiblePages === null) {
    return;
  }

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="pr-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="m-0 font-semibold">
                      Page {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Page ID"
                        instructions="Select the the page for this part of the task."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${nestIndex}.content.${index}.id`}
                        render={({ field }) => (
                          <FormControl
                            className="mt-16"
                            fullWidth
                            size="small"
                            error={
                              !!errors.content?.core?.[nestIndex]?.content?.[
                                index
                              ]?.id
                            }
                          >
                            <InputLabel id={`task-page-select-label-${index}`}>
                              Task Page
                            </InputLabel>
                            <Select
                              labelId={`task-page-select-label-${index}`}
                              id={`task-page-select-${index}`}
                              {...field}
                              label="Task Page"
                              value={
                                possiblePages.find(
                                  (page) => page.id === field.value
                                )
                                  ? field.value
                                  : ''
                              }
                            >
                              <MenuItem value="" disabled>
                                <em>Select a task page</em>
                              </MenuItem>
                              {possiblePages.map((page) => (
                                <MenuItem key={page.id} value={page.id}>
                                  {page.label} - {page.title}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {
                                errors.content?.core?.[nestIndex]?.content?.[
                                  index
                                ]?.id?.message
                              }
                            </FormHelperText>
                          </FormControl>
                        )}
                      />

                      <NumberedHeading
                        number="2"
                        title="Conditional?"
                        instructions="Is this task conditional? Only check this if first page of a task AND it is a task list that needs to be unlocked by another task being completed."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${nestIndex}.content.${index}.conditional`}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={(ev) => {
                                  field.onChange(ev.target.checked);
                                }}
                                checked={field.value}
                              />
                            }
                            label="Conditional?"
                          />
                        )}
                      />

                      <NumberedHeading
                        number="3"
                        title="Conditional Page ID"
                        instructions="Enter the page id that unlocks this part of the task. Only select from dropdown if first page of a task AND it is a task list that needs to be unlocked by another task being completed."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${nestIndex}.content.${index}.conditional_id`}
                        render={({ field }) => (
                          <FormControl
                            className="mt-16"
                            fullWidth
                            size="small"
                            error={
                              !!errors.content?.core?.[nestIndex]?.content?.[
                                index
                              ]?.conditional_id
                            }
                          >
                            <InputLabel
                              id={`conditional-page-select-label-${index}`}
                            >
                              Conditional Task Page
                            </InputLabel>
                            <Select
                              labelId={`conditional-page-select-label-${index}`}
                              id={`conditional-page-select-${index}`}
                              {...field}
                              label="Conditional Page ID"
                              value={
                                possiblePages.find(
                                  (page) => page.id === field.value
                                )
                                  ? field.value
                                  : ''
                              }
                            >
                              <MenuItem value="" disabled>
                                <em>Select a conditional page</em>
                              </MenuItem>
                              {possiblePages.map((page) => (
                                <MenuItem key={page.id} value={page.id}>
                                  {page.label} - {page.title}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {
                                errors.content?.core?.[nestIndex]?.content?.[
                                  index
                                ]?.conditional_id?.message
                              }
                            </FormHelperText>
                          </FormControl>
                        )}
                      />

                      <NumberedHeading
                        number="2"
                        title="Return?"
                        instructions="Does this page conclude the task? Only needs to be checked if it is not the last page possible AND there was a decision point so that it's on a branch that should end and return to environment. It keeps it from auto starting the next page."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${nestIndex}.content.${index}.return`}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={(ev) => {
                                  field.onChange(ev.target.checked);
                                }}
                                checked={field.value}
                              />
                            }
                            label="return?"
                          />
                        )}
                      />
                    </Box>
                    <Box
                      className="flex items-center mt-32 p-0 border-t"
                      sx={{ backgroundColor: 'background.default' }}
                    >
                      {index !== 0 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      )}
                      {index !== fields.length - 1 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      )}
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:minus-circle
                        </BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Page
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: '',
              conditional: false,
              conditional_id: '',
              return: false,
              return_id: ''
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Task Page
          </span>
        </Button>
      </Box>
    </>
  );
}
